import type { ArtifactPreview } from "~/services/artifacts";
import { ArtifactShell } from "./ArtifactShell";
import { DataframeSplit, DataframeTable } from "./DataframeTable";

// Mirrors `DataframePreview` in product/ai/lambdas/generate_preview/src/schemas.py:
//   { data: pandas-split JSON, displayedRows: int, totalRows: int }
// This is the only accepted shape — we don't fall back to records/bare-split
// because the preview Lambda is the sole producer.
interface DataframePreviewContent {
  data: DataframeSplit;
  displayedRows: number;
  totalRows: number;
}

function isDataframePreview(content: unknown): content is DataframePreviewContent {
  if (!content || typeof content !== "object" || Array.isArray(content)) return false;
  const c = content as Record<string, unknown>;
  if (typeof c.displayedRows !== "number" || typeof c.totalRows !== "number") return false;
  const split = c.data;
  if (!split || typeof split !== "object" || Array.isArray(split)) return false;
  const s = split as Record<string, unknown>;
  return Array.isArray(s.columns) && Array.isArray(s.data);
}

const INLINE_PAGE_SIZE = 10;
const EXPANDED_PAGE_SIZE = 25;

export const DataframeArtifact = ({ preview }: { preview: ArtifactPreview }) => {
  if (!isDataframePreview(preview.content)) {
    return (
      <ArtifactShell preview={preview}>
        <div className="py-2 text-sm text-destructive">Unexpected dataframe preview shape.</div>
      </ArtifactShell>
    );
  }

  const { data: split, displayedRows, totalRows } = preview.content;

  return (
    <ArtifactShell
      preview={preview}
      expandedContent={
        <DataframeTable
          split={split}
          pageSize={EXPANDED_PAGE_SIZE}
          displayedRows={displayedRows}
          totalRows={totalRows}
        />
      }
    >
      <DataframeTable
        split={split}
        pageSize={INLINE_PAGE_SIZE}
        displayedRows={displayedRows}
        totalRows={totalRows}
      />
    </ArtifactShell>
  );
};
